import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { MainLayout } from '../../components/layout/MainLayout';
import { orderCheckingService, OrderCheckDetail, OrderCheckItem } from '../../api/services/orderCheckingService';
import { salesOrderService } from '../../api/services/salesOrderService';
import { shippingMethodService, ShippingMethod } from '../../api/services/shippingMethodService';
import { useCustomers } from '../../hooks/useCustomers';

interface EditFormItem extends Omit<OrderCheckItem, 'Price'> {
  Price: string;
  DiscountPct?: string;
}

interface EditFormData {
  ShipToName: string;
  ShipToAddress: string;
  ShipToCity: string;
  ShipToState: string;
  ShipToZip: string;
  CustomerPO: string;
  ShipVia: string;
  Freight: string;
  Tax: string;
  Items: EditFormItem[];
}

const OrderCheckingDetail: React.FC = () => {
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderCheckDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [initials, setInitials] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [editForm, setEditForm] = useState<EditFormData | null>(null);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
  const [freightPerItem, setFreightPerItem] = useState(0);

  const { data: customers } = useCustomers();
  const editFormRef = useRef<EditFormData | null>(null);
  useEffect(() => { editFormRef.current = editForm; }, [editForm]);

  useEffect(() => {
    shippingMethodService.getAllShippingMethods().then(setShippingMethods).catch(() => {});
  }, []);

  useEffect(() => {
    if (invoiceNumber) {
      loadOrderDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  // Resolve freightPerItem once we have both order and customers
  useEffect(() => {
    if (!order || !customers) return;
    const customer = customers.find(c => c.CustomerID === order.CustomerID);
    setFreightPerItem(customer?.FreightPerItem ?? 0);
  }, [order, customers]);

  const NO_FREIGHT_PARTS = ['DS-1', 'DISCOUNT', 'SAMPLE', 'MISC-UNIVERSAL'];

  const calcAutoFreight = (items: EditFormItem[]): string | null => {
    if (freightPerItem <= 0) return null;
    const productCount = items.filter(
      i => i.PartNumber.trim() && !NO_FREIGHT_PARTS.includes(i.PartNumber.trim().toUpperCase())
    ).length;
    return (freightPerItem * productCount).toFixed(2);
  };

  // Re-run only when freightPerItem loads asynchronously after edit mode starts
  useEffect(() => {
    if (!isEditing || freightPerItem <= 0) return;
    setEditForm(prev => {
      if (!prev) return prev;
      const productCount = prev.Items.filter(
        i => i.PartNumber.trim() && !NO_FREIGHT_PARTS.includes(i.PartNumber.trim().toUpperCase())
      ).length;
      return { ...prev, Freight: (freightPerItem * productCount).toFixed(2) };
    });
  }, [freightPerItem, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOrderDetail = async () => {
    if (!invoiceNumber) return;
    try {
      setLoading(true);
      const data = await orderCheckingService.getOrderDetail(invoiceNumber);
      setOrder(data);
    } catch (error) {
      console.error('Error loading order detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToProduction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!order || !initials) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to send this order to production?')) return;
    try {
      setSubmitting(true);
      await orderCheckingService.sendToProduction({ invoiceNumber: order.InvoiceNumber, initials });
      navigate('/orders/check');
    } catch (error) {
      console.error('Error sending to production:', error);
      setSubmitting(false);
    }
  };

  const getNonDiscountSubtotal = (items: EditFormItem[]) =>
    items.reduce((sum, item) =>
      item.PartNumber.trim().toUpperCase() === 'DISCOUNT'
        ? sum
        : sum + (item.Quantity * (parseFloat(item.Price) || 0)),
      0);

  const getSubtotal = () => {
    if (isEditing && editForm) {
      return editForm.Items.reduce((sum, item) => sum + (item.Quantity * (parseFloat(item.Price) || 0)), 0);
    }
    if (!order) return 0;
    return order.Items.reduce((sum, item) => sum + item.Total, 0);
  };

  const handleDiscountPctChange = (index: number, pctStr: string) => {
    if (!editForm) return;
    const pct = parseFloat(pctStr) || 0;
    const base = getNonDiscountSubtotal(editForm.Items);
    const discountAmount = -(base * pct / 100);
    const updatedItems = [...editForm.Items];
    updatedItems[index] = {
      ...updatedItems[index],
      DiscountPct: pctStr,
      Price: pct > 0 ? discountAmount.toFixed(2) : '0',
    };
    setEditForm({ ...editForm, Items: updatedItems });
  };

  const getTotal = () => {
    const subtotal = getSubtotal();
    const freight = isEditing && editForm ? (parseFloat(editForm.Freight) || 0) : (order?.Freight || 0);
    const tax = isEditing && editForm ? (parseFloat(editForm.Tax) || 0) : (order?.Tax || 0);
    return subtotal + freight + tax;
  };

  const handleStartEdit = () => {
    if (!order) return;
    setEditForm({
      ShipToName: order.ShipToName || '',
      ShipToAddress: order.ShipToAddress || '',
      ShipToCity: order.ShipToCity || '',
      ShipToState: order.ShipToState || '',
      ShipToZip: order.ShipToZip || '',
      CustomerPO: order.CustomerPO || '',
      ShipVia: order.ShipVia || '',
      Freight: String(order.Freight || 0),
      Tax: String(order.Tax || 0),
      Items: order.Items.map(item => ({
        ...item,
        Price: String(item.Price),
        DiscountPct: item.PartNumber.trim().toUpperCase() === 'DISCOUNT' ? '' : undefined,
      }))
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm(null);
  };

  const handleSaveEdit = async () => {
    if (!order || !editForm || !invoiceNumber) return;
    try {
      setSaving(true);
      await orderCheckingService.updateOrder(invoiceNumber, {
        ShipToName: editForm.ShipToName,
        ShipToAddress: editForm.ShipToAddress,
        ShipToCity: editForm.ShipToCity,
        ShipToState: editForm.ShipToState,
        ShipToZip: editForm.ShipToZip,
        CustomerPO: editForm.CustomerPO,
        ShipVia: editForm.ShipVia,
        Freight: parseFloat(editForm.Freight) || 0,
        Tax: parseFloat(editForm.Tax) || 0,
        Items: editForm.Items.map(item => ({
          Id: item.Id,
          LineItemIndex: item.LineItemIndex,
          PartNumber: item.PartNumber,
          Description: item.Description,
          Quantity: item.Quantity,
          Price: parseFloat(item.Price) || 0
        }))
      });
      await loadOrderDetail();
      setIsEditing(false);
      setEditForm(null);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleItemChange = (index: number, field: 'Quantity' | 'Price' | 'PartNumber' | 'Description', value: number | string) => {
    if (!editForm) return;
    const updatedItems = [...editForm.Items];
    if (field === 'PartNumber' || field === 'Description' || field === 'Price') {
      updatedItems[index] = { ...updatedItems[index], [field]: String(value) };
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value as number };
    }
    setEditForm({ ...editForm, Items: updatedItems });
  };

  const FIXED_PRICE_CUSTOMERS = new Set(['33USA2017', 'C732480', 'C21337', 'C732510', 'C746657']);

  const handlePartNumberBlur = async (index: number) => {
    if (!editForm) return;
    const raw = editForm.Items[index].PartNumber.trim().toUpperCase();
    if (!raw) return;
    const updatedItems = [...editForm.Items];
    updatedItems[index] = { ...updatedItems[index], PartNumber: raw };
    // Recalculate freight using the committed part number (not intermediate keystrokes)
    const freightOnBlur = calcAutoFreight(updatedItems);
    setEditForm(prev => prev
      ? { ...prev, Items: updatedItems, ...(freightOnBlur !== null ? { Freight: freightOnBlur } : {}) }
      : prev
    );

    if (raw === 'DISCOUNT') {
      const part = await salesOrderService.lookupPart(raw, order?.CustomerID || undefined);
      setEditForm(prev => {
        if (!prev) return prev;
        const next = [...prev.Items];
        next[index] = {
          ...next[index],
          PartNumber: 'DISCOUNT',
          Description: next[index].Description || 'Discount',
          DiscountPct: next[index].DiscountPct ?? '',
          Price: next[index].Price ?? '0',
          LineItemIndex: part?.LineItemIndex ?? next[index].LineItemIndex,
        };
        const freight = calcAutoFreight(next);
        return { ...prev, Items: next, ...(freight !== null ? { Freight: freight } : {}) };
      });
      return;
    }

    const part = await salesOrderService.lookupPart(raw, order?.CustomerID || undefined);
    if (!part) return;

    const lockPrice = FIXED_PRICE_CUSTOMERS.has((order?.CustomerID || '').toUpperCase());

    setEditForm(prev => {
      if (!prev) return prev;
      const next = [...prev.Items];
      const keepPrice = parseFloat(next[index].Price) || 0;
      const resolvedPrice = !lockPrice && part.Price > 0 ? part.Price : keepPrice;
      next[index] = {
        ...next[index],
        Description: part.Description || next[index].Description,
        Price: String(resolvedPrice),
        Total: resolvedPrice * next[index].Quantity,
        LineItemIndex: part.LineItemIndex ?? next[index].LineItemIndex,
      };
      const freight = calcAutoFreight(next);
      return { ...prev, Items: next, ...(freight !== null ? { Freight: freight } : {}) };
    });
  };

  const handleDeleteItem = (index: number) => {
    if (!editForm) return;
    if (editForm.Items.length <= 1) {
      alert('Cannot delete the last item. Order must have at least one item.');
      return;
    }
    const newItems = editForm.Items.filter((_, i) => i !== index);
    const freight = calcAutoFreight(newItems);
    setEditForm({ ...editForm, Items: newItems, ...(freight !== null ? { Freight: freight } : {}) });
  };

  const handleAddItem = () => {
    if (!editForm) return;
    const newItem: EditFormItem = {
      Id: 0,
      Ordinal: editForm.Items.length + 1,
      PartNumber: '',
      Description: '',
      Quantity: 1,
      Price: '0',
      Total: 0,
      LineItemIndex: 0
    };
    const newItems = [...editForm.Items, newItem];
    // New blank row has no part number so freight won't change, but recalc for consistency
    const freight = calcAutoFreight(newItems);
    setEditForm({ ...editForm, Items: newItems, ...(freight !== null ? { Freight: freight } : {}) });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading order details...</p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!order) {
    return (
      <MainLayout>
        <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400">Order not found</p>
              <button
                onClick={() => navigate('/orders/check')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Back to Orders
              </button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Orders to be Checked
              </h2>
            </div>

            <div className="p-6">
              {/* Date and Action Buttons */}
              <div className="flex justify-between items-start mb-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Date: {format(new Date(order.OrderDate), 'M/d/yyyy')}
                </div>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleStartEdit}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => navigate('/orders/check')}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Back to Orders
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Sales Order Number */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Sales Order #: {order.InvoiceNumber}
              </h3>

              {/* Customer and Ship To */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Customer:</div>
                  <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded p-3">
                    <div className="text-gray-900 dark:text-gray-100">
                      <div><strong>Customer ID:</strong> {order.CustomerID}</div>
                      <div><strong>{order.BillToName}</strong></div>
                      {order.BillToAddress1 && <div>{order.BillToAddress1}</div>}
                      {order.BillToAddress2 && <div>{order.BillToAddress2}</div>}
                      {(order.BillToCity || order.BillToState || order.BillToZipCode) && (
                        <div>{order.BillToCity}{order.BillToCity && ', '}{order.BillToState} {order.BillToZipCode}</div>
                      )}
                      {order.CustomerEmail && <div>{order.CustomerEmail}</div>}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Ship To:</div>
                  <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded p-3">
                    {isEditing && editForm ? (
                      <div className="space-y-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Drop Ship? {order.IsDropShip ? 'Yes' : 'No'}
                        </div>
                        <input
                          type="text"
                          value={editForm.ShipToName}
                          onChange={(e) => setEditForm({ ...editForm, ShipToName: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          placeholder="Name"
                        />
                        <input
                          type="text"
                          value={editForm.ShipToAddress}
                          onChange={(e) => setEditForm({ ...editForm, ShipToAddress: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                          placeholder="Address"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editForm.ShipToCity}
                            onChange={(e) => setEditForm({ ...editForm, ShipToCity: e.target.value })}
                            className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="City"
                          />
                          <input
                            type="text"
                            value={editForm.ShipToState}
                            onChange={(e) => setEditForm({ ...editForm, ShipToState: e.target.value })}
                            className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="State"
                          />
                          <input
                            type="text"
                            value={editForm.ShipToZip}
                            onChange={(e) => setEditForm({ ...editForm, ShipToZip: e.target.value })}
                            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            placeholder="Zip"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-900 dark:text-gray-100">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Drop Ship? {order.IsDropShip ? 'Yes' : 'No'}
                        </div>
                        <div>{order.ShipToName}</div>
                        <div>{order.ShipToAddress}</div>
                        <div>{order.ShipToCity} {order.ShipToState} {order.ShipToZip}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* PO, Shipping Method, Payment Terms */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">PO #</div>
                  {isEditing && editForm ? (
                    <input
                      type="text"
                      value={editForm.CustomerPO}
                      onChange={(e) => setEditForm({ ...editForm, CustomerPO: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-gray-100">{order.CustomerPO || 'N/A'}</div>
                  )}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Shipping Method</div>
                  {isEditing && editForm ? (
                    <select
                      value={editForm.ShipVia}
                      onChange={(e) => setEditForm({ ...editForm, ShipVia: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Select shipping method</option>
                      {shippingMethods.map((sm) => (
                        <option key={sm.Id} value={sm.Name}>{sm.Name}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-gray-900 dark:text-gray-100">{order.ShipVia}</div>
                  )}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Payment Terms</div>
                  <div className="text-gray-900 dark:text-gray-100">{order.Terms}</div>
                </div>
              </div>

              {/* Order Items Table */}
              <div className="mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                      {isEditing && <th className="w-10 py-2 px-2"></th>}
                      <th className="text-left py-2 px-2 font-semibold text-gray-900 dark:text-white">Qty</th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-900 dark:text-white">Part #</th>
                      <th className="text-left py-2 px-2 font-semibold text-gray-900 dark:text-white">Description</th>
                      <th className="text-right py-2 px-2 font-semibold text-gray-900 dark:text-white">Unit Price</th>
                      <th className="text-right py-2 px-2 font-semibold text-gray-900 dark:text-white">Extension</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isEditing && editForm ? editForm.Items : order.Items).map((item, index) => (
                      <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                        {isEditing && (
                          <td className="py-3 px-2">
                            <button
                              type="button"
                              onClick={() => handleDeleteItem(index)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete item"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </td>
                        )}
                        <td className="py-3 px-2 text-gray-900 dark:text-gray-100">
                          {isEditing && editForm ? (
                            <input
                              type="number"
                              min="1"
                              value={item.Quantity}
                              onChange={(e) => handleItemChange(index, 'Quantity', parseInt(e.target.value) || 0)}
                              className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            />
                          ) : (
                            item.Quantity
                          )}
                        </td>
                        <td className="py-3 px-2 text-gray-900 dark:text-gray-100">
                          {isEditing && editForm ? (
                            <input
                              type="text"
                              value={item.PartNumber}
                              onChange={(e) => handleItemChange(index, 'PartNumber', e.target.value.toUpperCase())}
                              onBlur={() => handlePartNumberBlur(index)}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              placeholder="Part number"
                            />
                          ) : (
                            item.PartNumber
                          )}
                        </td>
                        <td className="py-3 px-2 text-gray-900 dark:text-gray-100">
                          {isEditing && editForm ? (
                            <input
                              type="text"
                              value={item.Description}
                              onChange={(e) => handleItemChange(index, 'Description', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              placeholder="Description"
                            />
                          ) : (
                            item.Description
                          )}
                        </td>
                        <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">
                          {isEditing && editForm ? (
                            (item as EditFormItem).PartNumber.trim().toUpperCase() === 'DISCOUNT' ? (
                              <div className="flex flex-col items-end gap-1">
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={(item as EditFormItem).DiscountPct ?? ''}
                                    onChange={(e) => handleDiscountPctChange(index, e.target.value)}
                                    className="w-20 px-2 py-1 border border-blue-400 dark:border-blue-500 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-right"
                                    placeholder="0"
                                  />
                                  <span className="text-gray-500 dark:text-gray-400 text-sm">%</span>
                                </div>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  = ${(parseFloat((item as EditFormItem).Price) || 0).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <input
                                type="text"
                                inputMode="decimal"
                                value={item.Price}
                                onChange={(e) => handleItemChange(index, 'Price', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-right"
                              />
                            )
                          ) : (
                            `$${(parseFloat(String(item.Price)) || 0).toFixed(2)}`
                          )}
                        </td>
                        <td className="py-3 px-2 text-right text-gray-900 dark:text-gray-100">
                          ${(isEditing && editForm
                            ? item.Quantity * (parseFloat(String(item.Price)) || 0)
                            : item.Total
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {isEditing && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded border border-blue-300 dark:border-blue-600"
                    >
                      + Add Item
                    </button>
                  </div>
                )}
              </div>

              {/* Totals + Note */}
              <div className="flex justify-between items-start mb-6 gap-6">
                <div className="flex-1">
                  {order.Note && (
                    <div className="h-full border border-gray-300 dark:border-gray-600 rounded p-3 bg-white dark:bg-gray-800">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Order Note</div>
                      <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">{order.Note}</div>
                    </div>
                  )}
                </div>
                <div className="w-full md:w-1/3 space-y-2 flex-shrink-0">
                  <div className="flex justify-between py-1 border-b border-gray-200 dark:border-gray-700">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Sub Total:</span>
                    <span className="text-gray-900 dark:text-gray-100">${getSubtotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-200 dark:border-gray-700">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Freight:</span>
                    {isEditing && editForm ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        value={editForm.Freight}
                        onChange={(e) => setEditForm({ ...editForm, Freight: e.target.value })}
                        className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-right"
                      />
                    ) : (
                      <span className="text-gray-900 dark:text-gray-100">${order.Freight.toFixed(2)}</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-200 dark:border-gray-700">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Tax:</span>
                    {isEditing && editForm ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        value={editForm.Tax}
                        onChange={(e) => setEditForm({ ...editForm, Tax: e.target.value })}
                        className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-right"
                      />
                    ) : (
                      <span className="text-gray-900 dark:text-gray-100">${(order?.Tax || 0).toFixed(2)}</span>
                    )}
                  </div>
                  <div className="flex justify-between py-2 border-t-2 border-gray-300 dark:border-gray-600">
                    <span className="font-bold text-gray-900 dark:text-white">Total:</span>
                    <span className="font-bold text-gray-900 dark:text-white">${getTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Duplicate PO Warning */}
              {order.InSystem && (
                <div className="bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded p-4 mb-6">
                  <p className="text-red-800 dark:text-red-100 font-semibold">
                    Error: PO Number <strong>{order.CustomerPO}</strong> is already in the System
                  </p>
                </div>
              )}

              {/* Checklist and Confirmation */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-orange-50 dark:bg-orange-900 border-l-4 border-orange-400 dark:border-orange-600 p-4 rounded">
                  <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-3">
                    Ask Yourself (other than just verifying Part Numbers):
                  </h4>
                  <ul className="space-y-2 text-sm text-orange-800 dark:text-orange-200">
                    <li className="flex items-start"><span className="mr-2">•</span><span>Is this being billed to the correct Customer/Dealer?</span></li>
                    <li className="flex items-start"><span className="mr-2">•</span><span>Is it being shipped to the correct Name & Address?</span></li>
                    <li className="flex items-start"><span className="mr-2">•</span><span>Is the customer PO correct?</span></li>
                    <li className="flex items-start"><span className="mr-2">•</span><span>Is the Shipping Method correct?</span></li>
                    <li className="flex items-start"><span className="mr-2">•</span><span>Is the customer's pricing on items correct?</span></li>
                    <li className="flex items-start"><span className="mr-2">•</span><span>Is the freight correct ($5 per item)?</span></li>
                    <li className="flex items-start"><span className="mr-2">•</span><span>Does the final Total look correct?</span></li>
                  </ul>
                </div>

                <div>
                  {order.ConfirmedBy ? (
                    <div className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-4">
                      <textarea
                        readOnly
                        value={order.ConfirmationNote || 'Confirmed from Atlas'}
                        className="w-full p-2 mb-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        rows={3}
                      />
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>Confirmed by {order.ConfirmedBy}</strong>
                        <br />
                        on {format(new Date(order.ConfirmedDate!), 'M/d/yy h:mm a')}
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSendToProduction}>
                      <div className={`bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-4 ${isEditing ? 'opacity-40 pointer-events-none select-none' : ''}`}>
                        <label htmlFor="initials" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Your Initials:
                        </label>
                        <input
                          type="text"
                          id="initials"
                          value={initials}
                          onChange={(e) => setInitials(e.target.value)}
                          required
                          maxLength={10}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                          placeholder="Enter your initials"
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => navigate('/orders/check')}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={!initials || order.InSystem || submitting}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {submitting ? 'Sending...' : 'Send to Production'}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default OrderCheckingDetail;
